<cmi-breadcrumb />
<h2 class="text-lg font-semibold mb-2 w-full border-b border-gray-300 pb-2">
  {{ 'contentProviders.properties' | translate }}
</h2>
@let provider = provider$ | async; @let properties = properties$ | async;
@if (provider && properties) {
  <ul class="space-y-2">
    @for (property of properties; track property.key) {
      <li>
        <div
          class="flex items-center justify-between py-4 hover:bg-gray-50 transition-colors"
          [title]="property.name">
          <div class="flex items-center gap-4 flex-1 min-w-0">
            <div class="shrink-0 items-center inline-flex text-gray-500">
              <cmi-provider-type-icon [providerType]="property.typeName" />
            </div>
            <div class="flex flex-col min-w-0 flex-1">
              <span class="text-base font-medium text-gray-900 truncate">
                {{ property.name }}
              </span>
              @if (property.formatString) {
                <span class="text-sm text-gray-500 truncate">
                  {{ 'contentProviders.format' | translate }}: {{ property.formatString }}
                </span>
              }
            </div>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            @if (supportsFormatting(property.typeName)) {
              <button
                class="text-main group rounded flex items-center justify-center"
                (click)="toggleFormattedInsert(property.key)"
                [title]="'contentProviders.formattedInsert' | translate">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                  data-slot="icon"
                  class="size-6 group-hover:fill-cmi-gray-light">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M5.248 20.246H9.05m0 0h3.696m-3.696 0 5.893-16.502m0 0h-3.697m3.697 0h3.803" />
                </svg>
              </button>
            }
            <button
              class="text-main group rounded flex items-center justify-center"
              (click)="addProperty(property, provider.key)"
              [title]="'contentProviders.add' | translate">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6 group-hover:fill-cmi-gray-light">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
            </button>
          </div>
        </div>
        @if (isPropertyExpanded(property.key) && supportsFormatting(property.typeName)) {
          <div class="px-4 pb-4 border-t border-gray-200 bg-gray-50">
            <cmi-property-format-form
              [property]="property"
              [initialFormatString]="property.formatString"
              [insertMode]="true"
              (applyFormat)="
                onApplyFormattedProperty(property, provider.key, $event.formatValue)
              " />
          </div>
        }
      </li>
    } @empty {
      <div class="text-center text-gray-500 py-8">
        {{ 'contentProviders.noPropertiesAvailable' | translate }}
      </div>
    }
  </ul>

  @if (provider && provider.parameters && provider.parameters.length > 0) {
    <div class="mt-8">
      <h2
        class="text-lg w-full font-semibold mb-2 inline-flex items-center gap-2 border-b border-gray-300 pb-2">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="size-6 fill-current">
          <path d="M11.625 16.5a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75Z" />
          <path
            fill-rule="evenodd"
            d="M5.625 1.5H9a3.75 3.75 0 0 1 3.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 0 1 3.75 3.75v7.875c0 1.035-.84 1.875-1.875 1.875H5.625a1.875 1.875 0 0 1-1.875-1.875V3.375c0-1.036.84-1.875 1.875-1.875Zm6 16.5c.66 0 1.277-.19 1.797-.518l1.048 1.048a.75.75 0 0 0 1.06-1.06l-1.047-1.048A3.375 3.375 0 1 0 11.625 18Z"
            clip-rule="evenodd" />
          <path
            d="M14.25 5.25a5.23 5.23 0 0 0-1.279-3.434 9.768 9.768 0 0 1 6.963 6.963A5.23 5.23 0 0 0 16.5 7.5h-1.875a.375.375 0 0 1-.375-.375V5.25Z" />
        </svg>
        {{ 'contentProviders.parametersTitle' | translate }}
      </h2>
      @for (parameter of provider.parameters; track parameter.name) {
        <a
          [routerLink]="[
            '/apps',
            tag$ | async,
            'providers',
            provider.key,
            'parameters',
            parameter.name
          ]"
          class="flex items-center group justify-between py-4 hover:bg-gray-50 transition-colors">
          <div class="flex items-center gap-4 flex-1 min-w-0">
            <div class="shrink-0 text-gray-500 items-center inline-flex group-hover:text-main">
              <cmi-provider-type-icon [providerType]="parameter.typeName" />
            </div>
            <div class="flex flex-col min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <span class="text-base font-medium text-gray-900 truncate group-hover:text-main">
                  {{ parameter.name }}
                </span>
              </div>
              <span class="text-sm text-gray-500 truncate">
                {{ 'contentProviders.type' | translate }}: {{ parameter.typeName }}
              </span>
            </div>
            <div class="shrink-0">
              <svg
                class="w-5 h-5 text-gray-400 stroke-current group-hover:text-main"
                fill="none"
                viewBox="0 0 20 20">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </a>
      }
    </div>
  }
} @else {
  <div class="text-center text-gray-500 py-8">
    {{ 'contentProviders.noPropertiesAvailable' | translate }}
  </div>
}
<ng-template #footerPortal>
  <cmi-search-bar class="w-full" (searchTermChanged)="onSearchTermChanged($event)" />
</ng-template>
